import { initMonitoring } from 'monitoring';

// todo check if this is actually needed on Windows

import 'normalize.css';
import 'react-virtualized/styles.css';
import './styles/app.less';
import React from 'react';
import { render } from 'react-dom';
import {
  initializeConnectionServices,
  onlineConnectionService,
} from '@neptune/shared/core-connection-domain';
import { initializeSso } from '@neptune/sso-business-logic';

import '@neptune/shared/venus-ui';

import { logger } from '@neptune/shared/core-logger-util';
import {
  authClient,
  consumeSlackEmbedCode,
  getKeycloakInitOptions,
  initNeptuneEmbedApi,
  initSlackEmbedAuth,
  installSlackEmbedAuthGuards,
  isAuthServerReachable,
  isSlackEmbedPathname,
  isSlackEmbedRenderMode,
  markNeptuneEmbedError,
  SlackEmbedSessionError,
} from '@neptune/shared/core-auth-domain';

import { AppInitErrorPage, SlackEmbedStatusPage } from '@neptune/shared/common-ui';
import { renderSplashScreen } from 'initialization/helpers';
import { handleFeatureFlags } from 'common/featureFlag';
import { localStorageMigrate } from './local-storage';
import { localStorageTopKey } from 'common/localStorage';
import { isBrowserAllowed } from '@neptune/shared/common-util';
import { BrowserNotAllowedInfo } from '@neptune/application-feature';
import { initializeTheme } from '@neptune/shared/theme-business-logic';
import * as Sentry from '@sentry/react';

const slackEmbed = isSlackEmbedPathname(window.location.pathname);
// The single-use code leaves the URL before error reporting starts, so Sentry never records it.
const slackEmbedCode = slackEmbed
  ? consumeSlackEmbedCode(window.location, window.history)
  : { code: undefined, urlCleared: true };

// Init error reporting {{{
// Sentry and user action tracking record the page URL, so they stay off for this page load when the
// embed code could not be removed from it; the embed still starts with the code kept in memory.
if (slackEmbedCode.urlCleared) {
  initMonitoring();
}
// Init error reporting }}}

if (slackEmbed) {
  initNeptuneEmbedApi(isSlackEmbedRenderMode(window.location.search) ? 'render' : 'interactive');
}

initializeTheme();

if (!isBrowserAllowed()) {
  render(<BrowserNotAllowedInfo />, document.getElementById('app'));
  throw new Error('Browser not allowed.');
}

initializeConnectionServices();
handleFeatureFlags();
printBackendInstance();
renderSplashScreen();

authClient.onAuthRefreshError = async () => {
  onlineConnectionService.goOnline();

  if (authClient.authenticated) {
    // eslint-disable-next-line no-console
    console.warn('Authentication: Auth refresh error, auth client is authenticated');
    logger.logMessage('Authentication: Auth refresh error, auth client is authenticated');
    return;
  }

  if (!(await isAuthServerReachable())) {
    // eslint-disable-next-line no-console
    console.warn('Authentication: Auth refresh error, auth server is not reachable');
    logger.logMessage('Authentication: Auth refresh error, auth server is not reachable');
    return;
  }

  // eslint-disable-next-line no-console
  console.warn('Authentication: Auth refresh error, auth client is not authenticated');
  logger.logMessage('Authentication: Auth refresh error, auth client is not authenticated');

  await logger.flush();

  await authClient.login({ prompt: 'none' });
};

authClient.onAuthLogout = async () => {
  // eslint-disable-next-line no-console
  console.log('Authentication: Logout triggered');
  logger.logMessage('Authentication: Logout triggered');
};

if (slackEmbed) {
  // Replaces the handlers above: the embed iframe must never navigate to the login page.
  installSlackEmbedAuthGuards(showSlackEmbedStatus);
}

const migrateLocalstorageToCurrentVersion = async () => {
  await localStorageMigrate(localStorageTopKey);
};

migrateLocalstorageToCurrentVersion()
  .then(async () => {
    await onlineConnectionService.ensureConnection();
  })
  .then(() => {
    Sentry.addBreadcrumb({
      category: 'authentication',
      level: 'info',
      message: 'Authentication: Initiating auth client',
      data: {
        startTime: sessionStorage.getItem('neptune-start-time'),
        now: new Date().toISOString(),
      },
    });
  })
  .then(() =>
    slackEmbed
      ? initSlackEmbedAuth(slackEmbedCode.code)
      : authClient.init(getKeycloakInitOptions()),
  )
  .then((authenticated) => {
    Sentry.addBreadcrumb({
      category: 'authentication',
      level: 'info',
      message: 'Authentication: Initiating auth client done',
      data: {
        authenticated,
        now: new Date().toISOString(),
        startTime: sessionStorage.getItem('neptune-start-time'),
      },
    });

    return authenticated;
  })
  .then(slackEmbed ? initializeSlackEmbed : initialize)
  .catch(slackEmbed ? showSlackEmbedError : showError)
  .finally(() => {
    sessionStorage.removeItem('neptune-start-time');
  });

async function initialize(authenticated) {
  await initializeSso({ authenticated });

  const desktopInit = await import('initialization/desktop');
  await desktopInit.initializeDesktopApp(authenticated);
}

async function initializeSlackEmbed(authenticated) {
  if (!authenticated) {
    throw new SlackEmbedSessionError('session_expired');
  }

  const slackEmbedInit = await import('initialization/slack-embed');
  await slackEmbedInit.initializeSlackEmbedApp();
}

function showSlackEmbedError(error) {
  if (!(error instanceof SlackEmbedSessionError)) {
    logger.logMessage('Slack embed initialization failed');

    if (error) {
      logger.logException(error, { module: 'index.js' });
    }
  }

  showSlackEmbedStatus(error instanceof SlackEmbedSessionError ? error.code : 'error');
}

function showSlackEmbedStatus(code) {
  const effectiveCode = markNeptuneEmbedError(code);

  render(<SlackEmbedStatusPage code={effectiveCode} />, document.getElementById('app'));
}

function showError(error) {
  logger.logMessage('Application initialization failed');

  if (error) {
    logger.logException(error, { module: 'index.js' });
  }

  render(<AppInitErrorPage />, document.getElementById('app'));
}

function printBackendInstance() {
  const neptuneInstance = process.env.NEPTUNE_INSTANCE;

  if (neptuneInstance) {
    // eslint-disable-next-line no-console
    console.log(`%cNeptune instance: ${neptuneInstance}`, 'color: #ff6600; font-size: 14px');
  }
}
